import { useState } from 'react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const handleDropdownToggle = (dropdown: string) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <a href="/" className="block">
              <img 
                src="https://static.readdy.ai/image/807f7a13fa3a4707437af02957aa93c9/1baf6cd348ab623aee3f3ee6959ea81e.png" 
                alt="Mortgage Moat" 
                className="h-20"
              />
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {/* Solutions Dropdown */}
            <div className="relative">
              <button
                onClick={() => handleDropdownToggle('solutions')}
                className="text-gray-700 hover:text-red-600 font-medium flex items-center gap-1"
              >
                Solutions
                <i className={`ri-arrow-down-s-line transition-transform ${activeDropdown === 'solutions' ? 'rotate-180' : ''}`}></i>
              </button>
              {activeDropdown === 'solutions' && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  <a href="/risk-assessment" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600">
                    Risk Assessment
                  </a>
                  <a href="/phishing-simulation" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600">
                    Phishing Simulation
                  </a>
                  <a href="/awareness-training" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600">
                    Awareness Training
                  </a>
                  <a href="/security-policies-manual" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600">
                    Security Policies Manual
                  </a>
                  <a href="/complete-security-management" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600">
                    Complete Security Management
                  </a>
                  <a href="/managed-services" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600">
                    Managed Services
                  </a>
                  <a href="/referral-partners" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600">
                    Referral Services
                  </a>
                </div>
              )}
            </div>

            {/* Knowledge Center Dropdown */}
            <div className="relative">
              <button
                onClick={() => handleDropdownToggle('knowledge')}
                className="text-gray-700 hover:text-red-600 font-medium flex items-center gap-1"
              >
                Knowledge Center
                <i className={`ri-arrow-down-s-line transition-transform ${activeDropdown === 'knowledge' ? 'rotate-180' : ''}`}></i>
              </button>
              {activeDropdown === 'knowledge' && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  <div className="relative group">
                    <div className="flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600 cursor-pointer">
                      <span>State Cybersecurity Requirements</span>
                      <i className="ri-arrow-right-s-line"></i>
                    </div>
                    <div className="hidden group-hover:block absolute left-full top-0 ml-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      <a href="/state-requirements/new-york" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600">
                        New York
                      </a>
                      <a href="/state-requirements/michigan" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600">
                        Michigan
                      </a>
                      <a href="/state-requirements/north-carolina" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600">
                        North Carolina
                      </a>
                      <a href="/state-requirements/north-dakota" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600">
                        North Dakota
                      </a>
                      <a href="/state-requirements/massachusetts" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600">
                        Massachusetts
                      </a>
                      <a href="/state-requirements/oregon" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600">
                        Oregon
                      </a>
                      <a href="/state-requirements/washington" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600">
                        Washington
                      </a>
                    </div>
                  </div>
                  <div className="border-t border-gray-200 my-2"></div>
                  <a href="/knowledge-center/human-focused-threats" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600">
                    Human-Focused Threats
                  </a>
                  <a href="/knowledge-center/malware-ransomware" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600">
                    Malware, Ransomware & System Exploits
                  </a>
                  <a href="/knowledge-center/access-identity-risks" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600">
                    Access & Identity Risks
                  </a>
                  <a href="/knowledge-center/third-party-supply-chain" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600">
                    Third-Party & Supply Chain Security
                  </a>
                  <a href="/knowledge-center/data-protection" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600">
                    Data Protection & Loss Prevention
                  </a>
                  <a href="/knowledge-center/business-continuity" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600">
                    Business Continuity & Incident Response
                  </a>
                </div>
              )}
            </div>
            
            <a href="/pricing" className="text-gray-700 hover:text-red-600 font-medium">
              Pricing
            </a>
            <a href="/our-story" className="text-gray-700 hover:text-red-600 font-medium">
              About
            </a>
            <a href="#contact" className="text-gray-700 hover:text-red-600 font-medium">
              Contact
            </a>
          </nav>

          {/* Client Portal Button */}
          <div className="hidden md:flex items-center">
            <a 
              href="/portal/login" 
              className="inline-flex items-center justify-center px-6 py-2.5 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap"
            >
              <i className="ri-shield-user-line mr-2 text-lg"></i>
              Client Portal
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-red-600"
            >
              <i className={`text-xl ${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-4">
              {/* Mobile Solutions */}
              <div>
                <button
                  onClick={() => handleDropdownToggle('mobile-solutions')}
                  className="text-gray-700 hover:text-red-600 font-medium flex items-center justify-between w-full"
                >
                  Solutions
                  <i className={`ri-arrow-down-s-line transition-transform ${activeDropdown === 'mobile-solutions' ? 'rotate-180' : ''}`}></i>
                </button>
                {activeDropdown === 'mobile-solutions' && (
                  <div className="mt-2 ml-4 space-y-2">
                    <a href="/risk-assessment" className="block text-gray-600 hover:text-red-600">
                      Upcoming Exam? Assess Your Risk
                    </a>
                    <a href="/managed-services" className="block text-gray-600 hover:text-red-600">
                      No Security Team? We'll Be Yours
                    </a>
                    <a href="/awareness-training" className="block text-gray-600 hover:text-red-600">
                      Stop Employees From Clicking
                    </a>
                    <a href="/security-policies-manual" className="block text-gray-600 hover:text-red-600">
                      Need Documentation? Get Compliant
                    </a>
                    <a href="/complete-security-management" className="block text-gray-600 hover:text-red-600">
                      Overwhelmed? We Handle Everything
                    </a>
                    <a href="/phishing-simulation" className="block text-gray-600 hover:text-red-600">
                      Test Your Team Before Hackers Do
                    </a>
                    <a href="/referral-partners" className="block text-gray-600 hover:text-red-600">
                      Referral Services
                    </a>
                  </div>
                )}
              </div>

              {/* Mobile Knowledge Center */}
              <div>
                <button
                  onClick={() => handleDropdownToggle('mobile-knowledge')}
                  className="text-gray-700 hover:text-red-600 font-medium flex items-center justify-between w-full"
                >
                  Knowledge Center
                  <i className={`ri-arrow-down-s-line transition-transform ${activeDropdown === 'mobile-knowledge' ? 'rotate-180' : ''}`}></i>
                </button>
                {activeDropdown === 'mobile-knowledge' && (
                  <div className="mt-2 ml-4 space-y-2">
                    <a href="/state-requirements" className="block text-gray-600 hover:text-red-600 text-sm font-semibold">
                      State Cybersecurity Requirements
                    </a>
                    <div className="border-t border-gray-200 my-2"></div>
                    <a href="/knowledge-center/human-focused-threats" className="block text-gray-600 hover:text-red-600 text-sm">
                      Human-Focused Threats
                    </a>
                    <a href="/knowledge-center/malware-ransomware" className="block text-gray-600 hover:text-red-600 text-sm">
                      Malware, Ransomware & System Exploits
                    </a>
                    <a href="/knowledge-center/access-identity-risks" className="block text-gray-600 hover:text-red-600 text-sm">
                      Access & Identity Risks
                    </a>
                    <a href="/knowledge-center/third-party-supply-chain" className="block text-gray-600 hover:text-red-600 text-sm">
                      Third-Party & Supply Chain Security
                    </a>
                    <a href="/knowledge-center/data-protection" className="block text-gray-600 hover:text-red-600 text-sm">
                      Data Protection & Loss Prevention
                    </a>
                    <a href="/knowledge-center/business-continuity" className="block text-gray-600 hover:text-red-600 text-sm">
                      Business Continuity & Incident Response
                    </a>
                  </div>
                )}
              </div>
              
              <a href="/pricing" className="text-gray-700 hover:text-red-600 font-medium">
                Pricing
              </a>
              <a href="/our-story" className="text-gray-700 hover:text-red-600 font-medium">
                About
              </a>
              <a href="#contact" className="text-gray-700 hover:text-red-600 font-medium">
                Contact
              </a>
              
              {/* Mobile Client Portal Button */}
              <div className="pt-4">
                <a 
                  href="/portal/login" 
                  className="flex items-center justify-center px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-all duration-200 shadow-md whitespace-nowrap"
                >
                  <i className="ri-shield-user-line mr-2 text-lg"></i>
                  Client Portal
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
